import { Box, VStack, SkeletonText } from '@chakra-ui/react';
import { FlexRow } from 'components/layout/FlexRow';
import EditableNote from 'components/widgets/Notes//EditableNote';
import Header from 'components/widgets/Notes/Header';
import ReadOnlyNote from 'components/widgets/Notes/ReadOnlyNote';
import { useProjectNotes } from 'hooks/useProjectNotes';
import { ProjectNotesClickActionTypes } from 'utils/analytics/constants';

interface NotesProps {
  targetId: number | string;
  readOnly?: boolean;
  trackingData?: {
    sectionName: string;
    subSectionName?: string;
    onTrack: (params: {
      projectId: number | string;
      clickAction: ProjectNotesClickActionTypes;
      clickText: string;
      sectionName: string;
      subSectionName?: string;
    }) => void;
  };
}

const vStack = {
  width: '100%',
  spacing: 'padding_4',
  pt: 'padding_16',
  pb: 'padding_16',
  pl: 0,
  pr: 0,
};

const outerBox = {
  display: 'flex',
  width: '100%',
  flexWrap: 'wrap',
};

const fullWidth = { width: '100%' };

const Notes = ({ targetId, readOnly, trackingData }: NotesProps) => {
  const { getClientProjectNotes } = useProjectNotes();

  const {
    data: notesData,
    isLoading: notesIsLoading,
    refetch: refetchNotes,
  } = getClientProjectNotes(targetId);

  const getNoteTargetId = (noteData: {
    project?: number | null;
    document?: string | null;
  }) => noteData.project ?? noteData.document ?? targetId;

  return (
    <VStack {...vStack}>
      <Box sx={outerBox}>
        <FlexRow sx={fullWidth}>
          <Header />
        </FlexRow>
        <SkeletonText isLoaded={!notesIsLoading}>
          {notesData &&
            notesData.map((noteData) => {
              const noteTargetId = getNoteTargetId(noteData);
              // This is not my note OR we are printing it into a PDF
              if (!noteData.is_editable || readOnly) {
                return (
                  <ReadOnlyNote
                    key={`project-note-${noteTargetId}-${noteData.updated_at}`}
                    targetId={noteTargetId}
                    note={noteData.note}
                    updated_at={noteData.updated_at}
                    first_name={noteData.first_name}
                    last_name={noteData.last_name}
                    email={noteData.email}
                  />
                );
              }

              // This is an existing note that we can modify.
              // This covers *editing* a note
              return (
                <EditableNote
                  targetId={noteTargetId}
                  note={noteData.note}
                  updated_at={noteData.updated_at}
                  first_name={noteData.first_name}
                  last_name={noteData.last_name}
                  email={noteData.email}
                  onNoteChanged={() => refetchNotes()}
                  trackingData={trackingData}
                />
              );
            })}
          {(!notesData ||
            (notesData && !notesData.some((noteData) => noteData.is_editable))) && (
            // There is no notes OR there are no notes that I can modify
            // This covers *adding* a note
            <EditableNote
              targetId={targetId}
              note=""
              updated_at=""
              first_name=""
              last_name=""
              email=""
              onNoteChanged={() => refetchNotes()}
              trackingData={trackingData}
            />
          )}
        </SkeletonText>
      </Box>
    </VStack>
  );
};

export default Notes;
